import React, { useMemo, useState } from "react";
import {
    Box, Grid, Paper, Typography, Tooltip, IconButton,
    CircularProgress, TablePagination, Chip,
} from "@mui/material";
import CategoryIcon from "@mui/icons-material/Category";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

import { SECTION_COLORS, ROWS_PER_PAGE } from "../../../constants";
import EmptyState from "../../shared/EmptyState";
import ErrorState from "../../shared/ErrorState";
import StatusChip from "../../shared/StatusChip";
import ListToolbar from "../../shared/ListToolbar";

const ProductCategoriesSection = ({
    productCategoriesApi,
    page,
    onPageChange,
    onView,
    onEdit,
    onDelete,
}) => {
    const [search, setSearch] = useState("");
    const [activeFilter, setActiveFilter] = useState("all");
    const [sortBy, setSortBy] = useState("name_asc");

    const sourceData = Array.isArray(productCategoriesApi.data) ? productCategoriesApi.data : [];

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        const rows = sourceData.filter((cat) => {
            if (activeFilter === "active" && !cat.active) return false;
            if (activeFilter === "inactive" && cat.active) return false;
            if (!q) return true;
            return String(cat.name || "").toLowerCase().includes(q)
                || String(cat.description || "").toLowerCase().includes(q)
                || String(cat.id || "").toLowerCase().includes(q);
        });
        rows.sort((a, b) => {
            if (sortBy === "created_desc") return new Date(b.created_at || 0) - new Date(a.created_at || 0);
            if (sortBy === "created_asc") return new Date(a.created_at || 0) - new Date(b.created_at || 0);
            if (sortBy === "updated_desc") return new Date(b.updated_at || 0) - new Date(a.updated_at || 0);
            return String(a.name || "").localeCompare(String(b.name || ""), "fr");
        });
        return rows;
    }, [sourceData, search, activeFilter, sortBy]);

    if (productCategoriesApi.error) {
        return <ErrorState error={productCategoriesApi.error} onRetry={() => productCategoriesApi.refetch()} />;
    }
    if (productCategoriesApi.loading) {
        return <Box py={4} display="flex" justifyContent="center"><CircularProgress size={28} /></Box>;
    }
    if (sourceData.length === 0) {
        return <EmptyState message="Aucune catégorie de produit configurée" color={SECTION_COLORS.rewards.main} />;
    }

    const maxPage = Math.max(0, Math.ceil(filtered.length / ROWS_PER_PAGE) - 1);
    const safePage = Math.min(page || 0, maxPage);
    const paginated = filtered.slice(safePage * ROWS_PER_PAGE, safePage * ROWS_PER_PAGE + ROWS_PER_PAGE);

    return (
        <Box>
            <ListToolbar
                search={search}
                onSearch={setSearch}
                activeFilter={activeFilter}
                onActiveFilter={setActiveFilter}
                searchPlaceholder="Rechercher une catégorie (nom, id)…"
                sortValue={sortBy}
                onSortChange={setSortBy}
                sortOptions={[
                    { value: "name_asc", label: "Nom (A-Z)" },
                    { value: "created_desc", label: "Création (récent)" },
                    { value: "created_asc", label: "Création (ancien)" },
                    { value: "updated_desc", label: "Mise à jour (récent)" },
                ]}
            />
            <Grid container spacing={2}>
                {paginated.map((cat) => (
                    <Grid item xs={12} sm={6} md={4} key={cat.id}>
                        <Paper elevation={0} sx={{
                            p: 2.5, borderRadius: 3, position: "relative", overflow: "hidden",
                            border: `1px solid ${SECTION_COLORS.rewards.main}18`,
                            background: `linear-gradient(135deg, ${SECTION_COLORS.rewards.main}06 0%, transparent 100%)`,
                            boxShadow: "0 8px 24px rgba(15, 23, 42, 0.10), 0 2px 8px rgba(15, 23, 42, 0.06)",
                            transition: "all 0.25s ease",
                            "&:hover": {
                                transform: "translateY(-2px)",
                                boxShadow: "0 14px 30px rgba(15, 23, 42, 0.14), 0 6px 14px rgba(15, 23, 42, 0.10)",
                                borderColor: `${SECTION_COLORS.rewards.main}30`,
                            },
                        }}>
                            <Box display="flex" alignItems="center" gap={1} mb={1}>
                                <CategoryIcon sx={{ color: SECTION_COLORS.rewards.main, fontSize: 22 }} />
                                <Typography fontWeight={700} fontSize={15} fontFamily="Montserrat, sans-serif"
                                    color="#1a1a2e" noWrap>
                                    {cat.name}
                                </Typography>
                            </Box>

                            {cat.description && (
                                <Typography fontSize={11} color="text.secondary" mb={1} sx={{ fontStyle: "italic" }}
                                    style={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                                    {cat.description}
                                </Typography>
                            )}

                            <Chip
                                label={`ID: ${cat.id?.slice(0, 8)}…`}
                                size="small"
                                sx={{ fontSize: 10, fontWeight: 600, bgcolor: "#f1f5f9", color: "#64748b", mb: 1.5, fontFamily: "monospace" }}
                            />

                            <Box display="flex" alignItems="center" justifyContent="space-between">
                                <StatusChip active={cat.active} />
                                <Box display="flex" gap={0.5}>
                                    <Tooltip title="Voir le détail">
                                        <IconButton size="small" onClick={() => onView(cat)}
                                            sx={{ color: SECTION_COLORS.rewards.main, bgcolor: `${SECTION_COLORS.rewards.main}10`, "&:hover": { bgcolor: `${SECTION_COLORS.rewards.main}20` }, width: 30, height: 30 }}>
                                            <VisibilityIcon sx={{ fontSize: 16 }} />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Modifier">
                                        <IconButton size="small" onClick={() => onEdit(cat)}
                                            sx={{ color: "#6366f1", bgcolor: "#eef2ff", "&:hover": { bgcolor: "#e0e7ff" }, width: 30, height: 30 }}>
                                            <EditIcon sx={{ fontSize: 16 }} />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Supprimer">
                                        <IconButton size="small" onClick={() => onDelete(cat)}
                                            sx={{ color: "#DC2626", bgcolor: "#fef2f2", "&:hover": { bgcolor: "#fee2e2" }, width: 30, height: 30 }}>
                                            <DeleteIcon sx={{ fontSize: 16 }} />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                            </Box>
                        </Paper>
                    </Grid>
                ))}
            </Grid>

            {filtered.length > ROWS_PER_PAGE && (
                <TablePagination
                    component="div"
                    count={filtered.length}
                    page={safePage}
                    onPageChange={onPageChange}
                    rowsPerPage={ROWS_PER_PAGE}
                    rowsPerPageOptions={[ROWS_PER_PAGE]}
                    sx={{ mt: 1, borderTop: "1px solid #f0f0f0" }}
                />
            )}
        </Box>
    );
};

export default ProductCategoriesSection;
